<template>
  <div class="review">
    <div class="review__avatar">
      <img
        v-if="!isAnonymized"
        :src="review.reviewer.profilePhotoUrl"
        :alt="review.reviewer.displayName"
      />
      <img
        v-else
        src="@/static/avatars/av.png"
        :alt="$t('message.reviews.userDataHidden')"
      />
    </div>
    <div class="review__content">
      <div class="review__header">
        <div class="review__reviewer-name">
          {{ reviewerName }}
        </div>
        <div class="review__meta">
          <StarsRating :rating="review.rating" />
          <span class="review__date">
            {{ `${$t("message.reviews.addedLabel")}: ${createDate}` }}
            <template v-if="isCreateAndUpdateDateDifferent">
              ({{ `${$t("message.common.edited")}: ${updateDate}` }})
            </template>
          </span>
        </div>
      </div>

      <div class="review__body">
        <p v-if="!isOnlyRateAdded" v-html="commentText"></p>
        <div v-else class="review__no-comment">
          <v-icon class="review__comment-icon">
            mdi-information
          </v-icon>
          <span>
            {{ $t("message.reviews.noComment") }}
          </span>
        </div>
        <span
          v-if="isCommentTooLong"
          class="review__show-more"
          @click="showMore = !showMore"
        >
          {{ expandButtonText }}
        </span>
      </div>
    </div>
  </div>
</template>

<script>
import moment from "moment";
import StarsRating from "@/components/ui/StarsRating";

export default {
  name: "ReviewPreview",
  components: {
    StarsRating
  },

  props: {
    review: {
      type: Object,
      required: true
    }
  },

  data() {
    return {
      showMore: false
    };
  },

  computed: {
    expandButtonText() {
      return this.showMore
        ? this.$t("message.common.showLess")
        : this.$t("message.common.showMore");
    },
    reviewerName() {
      return this.isAnonymized
        ? this.$t("message.reviews.userDataHidden")
        : this.review.reviewer.displayName;
    },
    createDate() {
      return this.parseDate(this.review.gmb_create_time);
    },
    updateDate() {
      return this.parseDate(this.review.gmb_update_time);
    },
    isOnlyRateAdded() {
      return !this.review.comment;
    },
    isCreateAndUpdateDateDifferent() {
      return (
        this.parseDate(this.review.gmb_create_time) !==
        this.parseDate(this.review.gmb_update_time)
      );
    },
    isAnonymized() {
      return this.review.disabled_collecting_personal_data;
    },
    isCommentTooLong() {
      return this.review.comment.length > 190;
    },
    commentText() {
      return this.review.comment.length > 190 && !this.showMore
        ? this.review.comment.slice(0, 190) + "..."
        : this.review.comment;
    }
  },

  methods: {
    parseDate(date) {
      return moment(date).format("DD.MM.YYYY");
    }
  }
};
</script>

<style lang="scss" scoped>
.review {
  display: flex;
  align-items: flex-start;
  gap: $spacing * 3;
  border: 1px solid $light-3;
  padding: $spacing * 2;
  border-radius: 16px;

  &__meta {
    display: flex;
    align-items: center;
    gap: $spacing;
  }

  &__date {
    @include font-size(12px);
    color: $gray-light;
    font-weight: 400;
  }

  &__avatar {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    overflow: hidden;
    flex-shrink: 0;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  &__content {
    display: flex;
    flex-direction: column;
    gap: $spacing * 1.5;
  }

  &__no-comment {
    @include font-size(12px);
    color: $gray-light;
    font-weight: 400;
    display: flex;
    align-items: center;
    gap: $spacing;
  }

  &__comment-icon {
    color: $gray-300;
  }

  &__body {
    color: $grey-darker;
    @include font-size(14px);
    line-height: 20px;
    max-width: 680px;
    position: relative;
    font-weight: 500;

    *:not(.review__no-comment) {
      display: inline;
    }
  }

  &__show-more {
    cursor: pointer;
    font-weight: 600;
  }

  &__reviewer-name {
    font-weight: 600;
    @include font-size(15px);
    color: $grey-darker;
    line-height: 18px;
    margin-bottom: 5px;
  }
}
</style>
